import React, { useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
// import Typography from '@mui/material/Typography';
import NaturePeopleIcon from '@mui/icons-material/NaturePeople';
import axios from 'axios';
import { useCreateCampMutation } from '../../redux/services';
import { useGetAllAffiliationsQuery } from '../../redux/services/affiliation-api';
import { useAuthContext } from '../../context/auth-context';
import { useEnqueueStacks } from '../../hooks/use-enqueue-stacks';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { baseUrls } from '../../constants';
import { RankingCategoriesEditor, PRESETS } from './ranking-categories-editor';
import { DirectorScoreDisplayEditor } from './director-score-display-editor';
import { SCORE_DISPLAY, SCORE_FORMAT } from '../../constants/director-constants';

const API = baseUrls.baseUrl;

export const CreateCampForm = ({ handleClose, onSuccess }) => {
  const { user, token } = useAuthContext();
  const [createCamp] = useCreateCampMutation();
  const { enqueueSuccess, enqueueError } = useEnqueueStacks();
  const { data: parents = [] } = useGetAllAffiliationsQuery({ type: 'parents' });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      name: '',
      dateInit: '',
      dateEnd: '',
      affiliation: '',
      logoUrl: '',
      rankingCategories: PRESETS.categorias,
      directorScoreDisplay: SCORE_DISPLAY.SEMAFORO,
      directorScoreFormat: SCORE_FORMAT.PORCENTAJE,
    },
    validationSchema: Yup.object({
      name: Yup.string().min(3, 'Mínimo 3 caracteres').required('Requerido'),
      dateInit: Yup.date().required('Requerido'),
      dateEnd: Yup.date()
        .min(Yup.ref('dateInit'), 'La fecha de fin debe ser posterior a la de inicio')
        .required('Requerido'),
      rankingCategories: Yup.array()
        .min(2, 'Mínimo 2 categorías')
        .of(Yup.object({
          name: Yup.string().required('Nombre requerido'),
          minPercentage: Yup.number().min(0).max(100).required(),
        })),
      directorScoreDisplay: Yup.string().oneOf(Object.values(SCORE_DISPLAY)).required(),
      directorScoreFormat: Yup.string().oneOf(Object.values(SCORE_FORMAT)).required(),
    }),
    onSubmit: async values => {
      if (!user?._id) {
        enqueueError('No se pudo identificar al usuario. Por favor vuelve a iniciar sesión.');
        return;
      }
      const sorted = [...values.rankingCategories].sort((a, b) => b.minPercentage - a.minPercentage);
      const result = await createCamp({
        name: values.name,
        dateInit: values.dateInit,
        dateEnd: values.dateEnd,
        userId: user._id,
        affiliation: values.affiliation || undefined,
        logoUrl: values.logoUrl || '',
        rankingCategories: sorted,
        directorScoreDisplay: values.directorScoreDisplay,
        directorScoreFormat: values.directorScoreFormat,
      });
      if (!result.error) {
        enqueueSuccess('Campamento creado exitosamente');
        if (onSuccess) onSuccess(result.data);
        handleClose();
      } else {
        enqueueError('Error al crear el campamento');
      }
    },
  });

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('logo', file);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/camp/upload-logo`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      formik.setFieldValue('logoUrl', data.url);
    } catch {
      enqueueError('Error al subir la imagen');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <Box component='form' onSubmit={formik.handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      <TextField
        label='Nombre'
        name='name'
        value={formik.values.name}
        onChange={formik.handleChange}
        error={formik.touched.name && Boolean(formik.errors.name)}
        helperText={formik.touched.name && formik.errors.name}
        required
        fullWidth
      />
      <TextField
        label='Fecha de Inicio'
        name='dateInit'
        type='date'
        value={formik.values.dateInit}
        onChange={formik.handleChange}
        error={formik.touched.dateInit && Boolean(formik.errors.dateInit)}
        helperText={formik.touched.dateInit && formik.errors.dateInit}
        required
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label='Fecha de Fin'
        name='dateEnd'
        type='date'
        value={formik.values.dateEnd}
        onChange={formik.handleChange}
        error={formik.touched.dateEnd && Boolean(formik.errors.dateEnd)}
        helperText={formik.touched.dateEnd && formik.errors.dateEnd}
        required
        fullWidth
        InputLabelProps={{ shrink: true }}
      />

      {/* Logo upload */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar src={formik.values.logoUrl || undefined} sx={{ width: 64, height: 64 }}>
          {!formik.values.logoUrl && !uploading && <NaturePeopleIcon />}
          {uploading && <CircularProgress size={24} sx={{ color: 'white' }} />}
        </Avatar>
        <Box>
          <input
            type='file'
            accept='image/png,image/jpeg'
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <Button
            variant='outlined'
            size='small'
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {formik.values.logoUrl ? 'Cambiar logo' : 'Subir logo'}
          </Button>
          {formik.values.logoUrl && (
            <Button
              variant='text'
              size='small'
              onClick={() => formik.setFieldValue('logoUrl', '')}
              sx={{
                mt: 0.5,
                minWidth: 'auto',
                px: 0,
                color: 'text.secondary',
                justifyContent: 'flex-start',
                '&:hover': { color: 'error.main', backgroundColor: 'transparent' }
              }}
            >
              Quitar logo
            </Button>
          )}
        </Box>
      </Box>

      <TextField
        select
        label='Organización (opcional)'
        name='affiliation'
        value={formik.values.affiliation}
        onChange={formik.handleChange}
        fullWidth
      >
        <MenuItem value=''>Sin organización</MenuItem>
        {parents.map(p => (
          <MenuItem key={p._id} value={p._id}>
            {p.name}{p.zone ? ` — ${p.zone}` : ''}
          </MenuItem>
        ))}
      </TextField>
      <RankingCategoriesEditor
        value={formik.values.rankingCategories}
        onChange={cats => formik.setFieldValue('rankingCategories', cats)}
      />
      <DirectorScoreDisplayEditor
        display={formik.values.directorScoreDisplay}
        format={formik.values.directorScoreFormat}
        onDisplayChange={v => formik.setFieldValue('directorScoreDisplay', v)}
        onFormatChange={v => formik.setFieldValue('directorScoreFormat', v)}
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
        <Button onClick={handleClose} variant='outlined'>Cancelar</Button>
        <Button type='submit' variant='contained' disabled={formik.isSubmitting || uploading}>
          Guardar
        </Button>
      </Box>
    </Box>
  );
};
